import {
  Component, Input, OnDestroy, OnInit,
} from '@angular/core';
import { UpdateMyCatalogSuggestionComponent } from './components/update-my-catalog-suggestion/update-my-catalog-suggestion.component';
import { MatDialog } from '@angular/material/dialog';
import { CatalogSuggestionsService } from 'src/app/services/catalog-suggestions.service';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { MyCatalogSuggestionDetailsComponent } from './components/my-catalog-suggestion-details/my-catalog-suggestion-details.component';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { ToastService } from 'src/app/services/toast.service';
import {
  NewTableData, TableBurgerClickEvent, TableRow, TableRowClickEvent, TableSortEvent,
} from 'src/app/components/new-table/new-table.component';
import { formatDate } from 'src/app/routes/manage-courses/components/course-catalog/components/update-course/course.class';
import { GeneralDataCell, StatusDataCell } from 'src/app/components/new-table/table-cell/table-cell.component';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { CatalogSuggestionSubmittedModalComponent } from './components/catalog-suggestion-submitted-modal/catalog-suggestion-submitted-modal.component';
import { formatDate as formatDateUtil } from 'src/app/utils/dates.utils';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-my-catalog-suggestions',
  templateUrl: './my-catalog-suggestions.component.html',
  styleUrls: ['./my-catalog-suggestions.component.scss'],
})
export class MyCatalogSuggestionsComponent implements OnInit, OnDestroy {
  _coursesData: any;
  coursesData$: Observable<any>;
  tableData: any;
  loading = false;

  searchQuery = '';
  offset = 0;
  selectedItemsPerPage = 25;
  sortColumn = 'courseName';
  sortDirection = 'ASC';

  // #region Subscription

  courseDataSubscription: Subscription;
  entityTimezoneSubscription: Subscription;
  // #endregion Subscription

  // #region new Table setup
  newTableData: NewTableData = {
    title: 'Catalog Suggestions',
    rowStyling: 'default',
    noDataFoundMessage: 'No catalog suggestions found',
    noDataButtonText: 'Submit Catalog Suggestion',
    columnTitles: [
      {
        name: 'Course Name & ID',
        align: 'left',
        sortable: true,
        sortByProp: 'courseName',
      },
      {
        name: 'Date & Time',
        align: 'left',
        sortable: true,
        sortByProp: 'dateOffered',
      },
      {
        name: 'Suggestion Date',
        align: 'left',
        sortable: true,
        isDefaultSort: true,
        sortByProp: 'courseCreatedDate',
        sortDirection: 'ASC',
      },
      {
        name: 'Status',
        align: 'left',
        sortable: true,
        sortByProp: 'currentCatalogStatusKid',
      },
    ],
    meta: {
      usePagination: true,
      totalItems: 0,
      curPage: 1,
      totalPages: 1,
      itemsPerPage: 25,
      links: {
        first: '',
        last: '',
        next: '',
        prev: '',
        self: '',
      },
    },
    data: [],
    hasBurgerMenu: true,
    burgerContent: [
      {
        content: 'View Suggestion Details',
        eventName: 'viewSuggestion',
      },
      // {
      //   content: 'Remove Suggestion',
      //   eventName: 'removeSuggestion',
      // },
    ],
  }

  // #endregion new Table setup

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private catalogSuggestionsService: CatalogSuggestionsService,
    private loadingService: LoadingSpinnerService,
    private toast: ToastService,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    if (!this.entityService.getLocalTimezone()) {
      this.entityService.getEntity('Config');
      this.entityTimezoneSubscription = this.entityService.entityData$.subscribe((entity: any) => {
        if (!entity) return;
        if (this._coursesData) this.searchForMyCatalogSuggestions();
        this.entityTimezoneSubscription?.unsubscribe();
      });
    }

    this.loading = true;
    this.searchForMyCatalogSuggestions();

    this.courseDataSubscription = this.catalogSuggestionsService.catalogSuggestionsData
      .subscribe((data: any) => {
        if (!data) return;

        this._coursesData = data;

        this.updateTableFromStoredData();

        this.loading = false;
      })
  }

  updateTableFromStoredData = () => {
    if (!this._coursesData?.rows) {
      this.newTableData.data = [];
      this.newTableData.meta = { ...this.newTableData.meta, totalItems: 0, totalPages: 0, curPage: 1 };
      return;
    }

    let rows = this._coursesData.rows;

    if (this.searchQuery?.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      rows = rows.filter((row: any) => {
        const courseName = row.courseName?.toLowerCase() || '';
        const courseId = row.id?.toString() || '';
        const description = row.courseDescription?.toLowerCase() || '';
        return courseName.includes(query) || courseId.includes(query) || description.includes(query);
      });
    }

    const totalItems = rows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / this.selectedItemsPerPage));
    const curPage = Math.min(
      Math.max(1, Math.floor(this.offset / this.selectedItemsPerPage) + 1),
      totalPages
    );

    const start = (curPage - 1) * this.selectedItemsPerPage;
    const pageRows = rows.slice(start, start + this.selectedItemsPerPage);

    const dataForPage = { ...this._coursesData, rows: pageRows };
    this.newTableData.data = this.formatTableData(dataForPage);
    this.newTableData.meta = {
      ...this.newTableData.meta,
      totalItems,
      totalPages,
      curPage,
      itemsPerPage: this.selectedItemsPerPage,
      usePagination: true,
      links: this._coursesData.links ?? this.newTableData.meta.links,
    };
  }

  searchForMyCatalogSuggestions = () => {
    this.loading = true;
    this.newTableData.data = []

    const params = {
      limit: this.selectedItemsPerPage,
      offset: this.offset,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      includedAssociations: 'User,Course',
      activeFlag: 1,
      // Remove filterString from params since backend doesn't filter properly
      // We'll filter client-side instead
    }

    this?.route?.snapshot?.params['id'] ?
      this.catalogSuggestionsService.getUserCatalogSuggestions(this?.route?.snapshot?.params['id'], params) :
      this.catalogSuggestionsService.getUserCatalogSuggestions(null, params)
  }

  // #region Table Formatting

  formatDateCell = (row: any): GeneralDataCell => {
    // FIX: CourseItems is nested in the catalogSuggestions response
    const courseItem = row.CourseItems?.[0];
    const preRecordedOption = courseItem?.preRecordedOption;

    const isAsync = preRecordedOption === 'ASYNC';
    const isAnytime = preRecordedOption === 'ANYTIME';

    if (isAsync) {
      return {
        type: 'general',
        mainText: 'Async',
        subText: 'Async',
      }
    }

    if (isAnytime) {
      return {
        type: 'general',
        mainText: formatDateUtil.asDateLocal(courseItem?.dateOffered) || 'Anytime',
        subText: 'Anytime',
      }
    }

    const tz = this.entityService.getLocalTimezoneWithFallback();
    const startDate = formatDateUtil.parseSessionTimeAsUtc(courseItem?.sessionStartDate);
    const endDate = formatDateUtil.parseSessionTimeAsUtc(courseItem?.sessionEndDate);
    const startTime = startDate ? formatDateUtil.asTimeWithTimezone(startDate, tz) : '';
    const endTime = endDate ? formatDateUtil.asTimeWithTimezone(endDate, tz) : '';

    return {
      type: 'general',
      mainText: formatDateUtil.asDateLocal(courseItem?.dateOffered),
      subText: `${startTime} - ${endTime}`,
    }
  }

  formatStatus = (statusKid: string) => {
    if (!statusKid) {
      statusKid = ''
    }

    // Handle both SPECIAL_REQUEST_ and SUGGESTION_ prefixes
    let status = statusKid;
    if (statusKid.includes('SPECIAL_REQUEST_')) {
      status = statusKid.split('SPECIAL_REQUEST_')[1];
    } else if (statusKid.includes('SUGGESTION_')) {
      status = statusKid.split('SUGGESTION_')[1];
    }

    const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    return capitalizedStatus;
  }

  formatSingleRow = (row: any) => {
    const courseItem = row.CourseItems?.[0];

    const courseInfoCell: GeneralDataCell = {
      type: 'general',
      mainText: row.courseName,
      subText: row.id, // FIX: Use row.id instead of row.courseItemId
    };

    const dateAndTimeCell: GeneralDataCell = this.formatDateCell(row);

    const requestDateCell: GeneralDataCell = {
      type: 'general',
      mainText: formatDate(row.createdDate, true), // FIX: Use row.createdDate, not row.courseCreatedDate
      mainFontStyle: 'normal',
    };

    const statusCell: StatusDataCell = {
      type: 'status',
      statusDisplay: this.formatStatus(row.currentCatalogStatusKid),
      statusKid: row.currentCatalogStatusKid,
      statusType: 'courseCatalog',
      forceUppercase: false,
    };

    const rowCells = [
      courseInfoCell,
      dateAndTimeCell,
      requestDateCell,
      statusCell,
    ];

    const formattedRow: TableRow = {
      itemId: Number(row.id), // FIX: This is the courseId we need for the API call
      columnData: rowCells,
    };

    return formattedRow;
  }

  formatTableData = (data: any) => {
    // FIX: The service normalizes data.catalogSuggestions into data.rows
    // So we should always use data.rows here
    const rows = data.rows || [];

    if (!rows || rows.length === 0) {
      console.log('[Component] No rows to format');
      return [];
    }

    const formattedData = rows.map(this.formatSingleRow);

    return formattedData;
  }

  formatTableMeta = (data: any): NewTableData['meta'] => {
    const pagination = data.pagination;
    const links = data.links;

    return {
      curPage: pagination.curPage,
      itemsPerPage: pagination.totalPerPage,
      totalItems: pagination.totalItems,
      totalPages: pagination.totalPages,
      usePagination: true,
      links: {
        first: links.first,
        last: links.last,
        next: links.next,
        prev: links.prev,
        self: links.self,
      },
    }
  }

  // #endregion Table Formatting

  // #region Event Handlers
  searchQueryChanged = (searchQuery: string) => {
    this.searchQuery = searchQuery;
    this.offset = 0;
    this.updateTableFromStoredData();
  }

  handleRowClick = (id: TableRowClickEvent) => {
    this.showItemDetails(id);
  }

  handleMeatballClick = (event: TableBurgerClickEvent) => {
    console.log('handleMeatballClick', event);
    if (event.eventName === 'viewSuggestion') {
      this.showItemDetails(event.itemId);
    }

    if (event.eventName === 'removeSuggestion') {
      const itemToRemove = { itemId: event.itemId };
      this.removeItem(itemToRemove);
    }
  }

  handleSortEvent = (event: TableSortEvent) => {
    this.sortColumn = event.columnName;
    this.sortDirection = event.sortDirection;

    this.searchForMyCatalogSuggestions();
  }

  openCreateModal = () => { }
  // #endregion Table Click Handlers

  // #region Filter, Sort and Pagination Handling
  onPageChanged(page: number) {
    this.offset = this.calculateOffset(page);
    this.updateTableFromStoredData();
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.updateTableFromStoredData();
  }

  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  // #endregion Filter, Sort and Pagination Handling

  toggleCreateModal = () => {
    this.dialog
      .open(UpdateMyCatalogSuggestionComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result?.statusCode === 1000) {
          // refresh list
          this.searchForMyCatalogSuggestions();

          this.toast.setToast({
            text: 'Catalog suggestion submitted successfully',
            type: 'success',
            dismissible: false,
            icon: true,
          })

          // success modal
          this.dialog.open(CatalogSuggestionSubmittedModalComponent, {
            width: '400px',
            height: '340px',
            maxWidth: '400px',
            maxHeight: '340px',
            panelClass: 'catalog-success-dialog',
            disableClose: true,
            autoFocus: false,
          });
        }
      });
  };


  showItemDetails = (item: number) => {
    // FIX: Use the normalized rows array, and also check catalogSuggestions as fallback
    const selectedCourse = this._coursesData?.rows?.find((course: any) => course.id == item)
      || this._coursesData?.catalogSuggestions?.find((course: any) => course.id == item);

    if (!selectedCourse) {
      console.error('[Component] Could not find course with id:', item);
      return;
    }

    const dialogRef = this.dialog.open(MyCatalogSuggestionDetailsComponent, { data: selectedCourse })
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'cancel-suggestion') {
        const itemToRemove = { itemId: selectedCourse.id };
        this.removeItem(itemToRemove);
      }
    })
  }

  removeItem = (item: any) => {
    // FIX: Use the normalized rows array, and also check catalogSuggestions as fallback
    const selectedCourse = this._coursesData?.rows?.find((course: any) => course.id == item.itemId)
      || this._coursesData?.catalogSuggestions?.find((course: any) => course.id == item.itemId);

    if (!selectedCourse) {
      console.error('[Component] Could not find course with id:', item.itemId);
      return;
    }

    const verificationRef = this.dialog.open(VerificationComponent, {
      data: {
        type: 'alert',
        title: 'Are you sure?',
        confirmButtonTitle: 'Yes, Delete Suggestion',
        cancelButtonTitle: 'Cancel',
        text: 'Are you sure you want to delete this catalog suggestion? This action cannot be undone.',
      },
    })
    verificationRef.afterClosed().subscribe((result) => {
      if (result === 'verified') {
        this.loadingService.setIsLoading(true);

        this.catalogSuggestionsService.removeCatalogSuggestion(selectedCourse.id)
          .subscribe({
            next: () => {
              this.toast.setToast({
                text: 'Catalog suggestion removed successfully',
                type: 'success',
                dismissible: false,
                icon: true,
              })
              this.loadingService.setIsLoading(false);
              this.searchForMyCatalogSuggestions();
            },
            error: (error) => {
              this.toast.setToast({
                text: 'There was an error removing the course',
                type: 'error',
                dismissible: false,
                icon: true,
              })
              this.loadingService.setIsLoading(false);
              this.searchForMyCatalogSuggestions();

              throw error;
            },
          })
      }
    })
  }

  ngOnDestroy(): void {
    this.courseDataSubscription?.unsubscribe();
    this.entityTimezoneSubscription?.unsubscribe();
  }

}
